// Registry of the providers behind each dashboard data stream. Keys are the `organization` values exported by src/config/*Config.js.
// Internal keys (organization, collection) are never shown to users; only provider names, licences and notes are.
// Licences are listed only where dataset_licenses.json confirms them (never for PLACEHOLDER rows); notes carry display restrictions.
//
// SourceEntry = { provider: string, url?: string, license?: { name: string, url: string }, note?: string }
// DATA_SOURCES[organization] = { default: SourceEntry[], collections?: { [collection]: SourceEntry[] } }
// `collections` keys are the collection identifiers used in the matching config file; they override `default` for that collection.

const CC_BY_4 = { name: "CC BY 4.0", url: "https://creativecommons.org/licenses/by/4.0/" };
const FAO_TERMS = { name: "FAO Statistical Database Terms of Use", url: "https://www.fao.org/contact-us/terms/db-terms-of-use/en/" };

const FAO = "Food and Agriculture Organization of the United Nations (FAO)";
const faostat = (code, license = CC_BY_4, note) => ({
	provider: FAO,
	url: `https://www.fao.org/faostat/en/#data/${code}`,
	license,
	...(note && { note }),
});

const ECOREADY_INTERNAL = "ECO-READY project (internally generated)";

const ECOVITALL = "Ecovitall Living Lab (Hungary)";
const ecovitallOpen = [{ provider: ECOVITALL, license: CC_BY_4 }];

const CONCAT_PROVIDER = "CONCAT Living Lab (Spain) / IRTA";
const CONCAT_NOTE = "Confidential data (ECO-READY Sub-grant Agreement, Art. 6), shown as plots only. Any other use requires prior IRTA approval.";

// Magnet model outputs; the PSILCA footer on the Magnet screen credits GreenDelta. Exported so the LCA tab can credit each stream separately.
export const MAGNET_MODEL_SOURCE = { provider: "MAGNET model (WUR, JRC, Thünen)", note: "Academic and research use; consortium terms apply." };
export const PSILCA_SOURCE = { provider: "GreenDelta GmbH (PSILCA database v3.1.1)", note: "All rights to the data remain with GreenDelta. No raw data is disclosed." };

const SWEGREEN = [{
	provider: "SweGreen AB (SECO Collaboration, Sweden)",
	note: "The dataset remains the intellectual property of SweGreen AB and is displayed here with permission. Any further reuse, redistribution or commercial use requires prior written permission from SweGreen AB.",
}];

export const DATA_SOURCES = {
	efsa: { default: [{ provider: "European Food Safety Authority (EFSA)", url: "https://www.efsa.europa.eu" }] },

	european_data: {
		default: [{
			provider: "European Commission, DG AGRI (Agri-food Data Portal)",
			url: "https://agridata.ec.europa.eu",
			license: CC_BY_4,
		}],
	},

	faostat: {
		default: [{ provider: FAO, url: "https://www.fao.org/faostat", license: FAO_TERMS }],
		collections: {
			cropland_nutrient_balance: [
				faostat("ESB"),
				{ provider: "International Fertilizer Association (IFA)", note: "Data sourced from IFA may have additional restrictions." },
			],
			emissions_from_crops: [faostat("GCE")],
			emissions_from_energy_use: [faostat("GN")],
			land_cover: [
				faostat("LC", FAO_TERMS),
				{ provider: "European Space Agency (ESA)", note: "Source land cover maps (MODIS, ESA CCI-LC, Copernicus CGLS, WorldCover) have their own licences." },
			],
			land_use: [faostat("RL", { name: "CC BY-NC-SA 3.0 IGO", url: "https://creativecommons.org/licenses/by-nc-sa/3.0/igo/" }, "Non-commercial, share-alike.")],
			livestock_patterns: [faostat("EK")],
			food_security_indicators: [faostat("FS")],
			temperature_change: [
				faostat("ET"),
				{ provider: "NASA Goddard Institute for Space Studies (GISS)" },
			],
		},
	},

	// Disaster impacts (collection "maize_") and early-warning forecasts (collection "warming_level") are both generated inside the project.
	climate_impact_data: {
		default: [{ provider: ECOREADY_INTERNAL }],
		collections: {
			maize_: [{ provider: ECOREADY_INTERNAL, note: "Maize climate disaster impact estimates." }],
			warming_level: [{ provider: ECOREADY_INTERNAL, note: "Early warning crop yield forecasts. Forecasts are uncertain." }],
		},
	},

	// The PSILCA footer on the Magnet screen credits GreenDelta; the model outputs themselves are MAGNET (consortium terms).
	magnet_data: { default: [MAGNET_MODEL_SOURCE, PSILCA_SOURCE] },

	// LCA tab of every Living Lab. The full line is "<provider>, derived from <lab provider> data"; see getLcaSources. The row is PLACEHOLDER in dataset_licenses.json, so no licence.
	lca: { default: [{ provider: "ECO-READY LCA" }] },

	aidemec: { default: [{ provider: "AIDEMEC Living Lab (Italy)" }] },

	concat_ll: {
		default: [{ provider: CONCAT_PROVIDER, note: CONCAT_NOTE }],
		collections: {
			wheat__data: [{ provider: CONCAT_PROVIDER, note: `CONCAT Wheat Production Dataset developed by IRTA. ${CONCAT_NOTE}` }],
			weather_data: [{ provider: CONCAT_PROVIDER, note: CONCAT_NOTE }],
		},
	},

	// One collection (weather_data) is filtered by station name and the frontend cannot tell which stations are IMGW-PIB reference data
	// and which are Weenat sensors, so both providers are credited for it.
	ecoready_masuria: {
		default: [{ provider: "EcoReady Masuria Living Lab (Poland)" }],
		collections: {
			weather_data: [
				{
					provider: "IMGW-PIB (Institute of Meteorology and Water Management – National Research Institute)",
					url: "https://danepubliczne.imgw.pl/",
					license: {
						name: "Polish Public Sector Information reuse rules",
						url: "https://www.gov.pl/web/rozwoj-technologia/prawna-ochrona-informacji-sektora-publicznego",
					},
					note: "Źródłem pochodzenia danych jest Instytut Meteorologii i Gospodarki Wodnej – Państwowy Instytut Badawczy. Dane zostały przetworzone. (Source: IMGW-PIB. Data modified (aggregated) by ECO-READY.)",
				},
				{
					provider: "Weenat (IoT sensor stations, EcoReady Masuria Living Lab, Poland)",
					note: "Proprietary data, shown for non-commercial visualisation only. Downloading, extraction or commercial reuse is prohibited.",
				},
			],
		},
	},

	// Spectroscopy / NIR data is embargoed; the other collections are CC BY 4.0 (environment, yield, resource use, consumer feedback).
	ecovitall: {
		default: ecovitallOpen,
		collections: {
			experiment_data: ecovitallOpen,
			resource_consumption: ecovitallOpen,
			consumer_panel: ecovitallOpen,
			sensory_evaluations: ecovitallOpen,
			nir_reports: [{
				provider: `${ECOVITALL} / academic partner`,
				note: "Embargoed: jointly generated with an academic partner whose findings are not yet published.",
			}],
		},
	},

	esappin: { default: [{ provider: "ESAPPIN Living Lab (Germany)" }] },
	livorganic: { default: [{ provider: "LivOrganic Living Lab (Denmark)" }] },
	lofs: { default: [{ provider: "LOFS Living Lab" }] },
	probio: { default: [{ provider: "PROBIO Living Lab (Czech Republic)" }] },

	seco_collab: {
		default: SWEGREEN,
		collections: {
			environmental_data: SWEGREEN,
			energy_consumption: SWEGREEN,
		},
	},

	thalla: { default: [{ provider: "THALLA Living Lab (Greece)" }] },
};

// Returns the entries for a collection when the organization has one for it, else the organization default.
// Development: an unknown organization throws so a missing key is caught early. Production: returns [] so the UI never crashes.
export const getSources = (organization, collection) => {
	const entry = DATA_SOURCES[organization];
	if (!entry) {
		if (process.env.NODE_ENV !== "production") throw new Error(`No data sources registered for organization "${organization}"`);
		return [];
	}
	return (collection && entry.collections?.[collection]) || entry.default;
};

// Combines source lists while preserving their order and retaining distinct notes or licenses.
export const mergeSources = (...sourceLists) => {
	const seen = new Set();

	return sourceLists.flat().filter((source) => {
		const key = JSON.stringify([
			source.provider,
			source.url,
			source.license?.name,
			source.license?.url,
			source.note,
		]);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

// Entries for the LCA tab of a Living Lab: one "ECO-READY LCA, derived from <lab provider> data" line per lab provider,
// carrying the lab's own restriction note (for example the CONCAT confidentiality note).
export const getLcaSources = (organization) => {
	const [{ provider: lca }] = DATA_SOURCES.lca.default;
	return getSources(organization).map(({ provider, note }) => ({
		provider: `${lca}, derived from ${provider} data`,
		...(note && { note }),
	}));
};
